import { makeChoices, makeTextChoices } from "../distractors";
import { clean, frac, numStr, numUnit, simplify, text } from "../format";
import type { QuestionDraft, Rng, TopicDef } from "../types";
import { both, byTier, fraction, numeric, q, textAnswer } from "./helpers";

const CM2 = { pl: "cm²", en: "cm²" };
const CM = { pl: "cm", en: "cm" };
const cm2 = (n: number) => numUnit(n, "cm²");
const cm = (n: number) => numUnit(n, "cm");

// ---------------------------------------------------------------- 1. big numbers
function bigNumbers(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const n = rng.int(21, 999);
      const rounded = Math.round(n / 10) * 10;
      const choice4 = makeChoices(
        rng,
        rounded,
        [rounded + 10, rounded - 10, Math.round(n / 100) * 100],
        (r) => r.int(2, 99) * 10,
      );
      return q(
        both(`Zaokrąglij liczbę ${n} do dziesiątek.`, `Round ${n} to the nearest ten.`),
        numeric(rounded),
        [
          `Patrzymy na cyfrę jedności: ${n % 10}. ${n % 10 >= 5 ? "5 lub więcej – zaokrąglamy w górę." : "Mniej niż 5 – zaokrąglamy w dół."}`,
          `${n} ≈ ${rounded}.`,
        ],
        [
          `Look at the ones digit: ${n % 10}. ${n % 10 >= 5 ? "5 or more – round up." : "Less than 5 – round down."}`,
          `${n} ≈ ${rounded}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const toHundreds = rng.chance(0.5);
      const n = rng.int(1050, 999999);
      const unit = toHundreds ? 100 : 1000;
      const rounded = Math.round(n / unit) * unit;
      const digit = Math.floor((n % unit) / (unit / 10));
      const choice4 = makeChoices(
        rng,
        rounded,
        [rounded + unit, rounded - unit, Math.round(n / (unit * 10)) * unit * 10],
        (r) => r.int(2, 999) * unit,
      );
      return q(
        both(
          `Zaokrąglij liczbę ${n} do ${toHundreds ? "setek" : "tysięcy"}.`,
          `Round ${n} to the nearest ${toHundreds ? "hundred" : "thousand"}.`,
        ),
        numeric(rounded),
        [
          `Patrzymy na cyfrę ${toHundreds ? "dziesiątek" : "setek"}: ${digit}. ${digit >= 5 ? "Zaokrąglamy w górę." : "Zaokrąglamy w dół."}`,
          `${n} ≈ ${rounded}.`,
        ],
        [
          `Look at the ${toHundreds ? "tens" : "hundreds"} digit: ${digit}. ${digit >= 5 ? "Round up." : "Round down."}`,
          `${n} ≈ ${rounded}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const th = rng.int(1, 9);
      const h = rng.int(0, 9);
      const t = rng.int(0, 9);
      const o = rng.int(0, 9);
      const value = th * 1000 + h * 100 + t * 10 + o;
      const choice4 = makeChoices(
        rng,
        value,
        [th * 100 + h * 10 + t + o, Number(`${th}${h}${o}${t}`), value + 100],
        (r) => r.int(1000, 9999),
      );
      return q(
        both(
          `Zapisz liczbę: ${th} tysiące, ${h} setek, ${t} dziesiątek i ${o} jedności.`,
          `Write the number: ${th} thousands, ${h} hundreds, ${t} tens and ${o} ones.`,
        ),
        numeric(value),
        [`${th}·1000 + ${h}·100 + ${t}·10 + ${o}·1.`, `= ${value}.`],
        [`${th}×1000 + ${h}×100 + ${t}×10 + ${o}×1.`, `= ${value}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 2. written multiplication
function writtenMult(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const [a, b] = byTier(
    tier,
    () => [rng.int(12, 99), rng.int(3, 9)],
    () => [rng.int(12, 99), rng.int(11, 39)],
    () => [rng.int(102, 499), rng.int(11, 29)],
  );
  const v = a * b;
  const choice4 = makeChoices(rng, v, [a * (b + 1), a * b + a, a * (b - 1), a + b], (r) => r.int(50, 15000));
  const tens = Math.floor(b / 10) * 10;
  const ones = b % 10;
  const stepsPl =
    b >= 10
      ? [
          `Rozbijamy ${b} na ${tens} i ${ones}.`,
          `${a} · ${tens} = ${a * tens}, ${a} · ${ones} = ${a * ones}.`,
          `${a * tens} + ${a * ones} = ${v}.`,
        ]
      : [
          `${a} · ${b}: mnożymy jedności i dziesiątki.`,
          `${Math.floor(a / 10) * 10} · ${b} = ${Math.floor(a / 10) * 10 * b}, ${a % 10} · ${b} = ${(a % 10) * b}, razem ${v}.`,
        ];
  const stepsEn =
    b >= 10
      ? [
          `Split ${b} into ${tens} and ${ones}.`,
          `${a} × ${tens} = ${a * tens}, ${a} × ${ones} = ${a * ones}.`,
          `${a * tens} + ${a * ones} = ${v}.`,
        ]
      : [
          `${a} × ${b}: multiply tens and ones.`,
          `${Math.floor(a / 10) * 10} × ${b} = ${Math.floor(a / 10) * 10 * b}, ${a % 10} × ${b} = ${(a % 10) * b}, together ${v}.`,
        ];
  return q(
    both(`Oblicz pisemnie: ${a} · ${b} = ?`, `Calculate: ${a} × ${b} = ?`),
    numeric(v),
    stepsPl,
    stepsEn,
    { choice4 },
  );
}

// ---------------------------------------------------------------- 3. written division
function writtenDiv(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const d = rng.int(2, 9);
      const qv = rng.int(3, 12);
      const n = d * qv;
      const choice4 = makeChoices(rng, qv, [qv + 1, qv - 1, n - d], (r) => r.int(2, 20));
      return q(
        both(`Oblicz: ${n} : ${d} = ?`, `Calculate: ${n} ÷ ${d} = ?`),
        numeric(qv),
        [
          `Szukamy liczby, która pomnożona przez ${d} daje ${n}.`,
          `${d} · ${qv} = ${n}, więc ${n} : ${d} = ${qv}.`,
        ],
        [`Find the number that times ${d} gives ${n}.`, `${d} × ${qv} = ${n}, so ${n} ÷ ${d} = ${qv}.`],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(3, 9);
      const qv = rng.int(21, 120);
      const n = d * qv;
      const choice4 = makeChoices(rng, qv, [qv + 1, qv - 1, qv + 10], (r) => r.int(20, 130));
      return q(
        both(`Oblicz pisemnie: ${n} : ${d} = ?`, `Calculate: ${n} ÷ ${d} = ?`),
        numeric(qv),
        [
          `Dzielimy kolejno setki, dziesiątki i jedności przez ${d}.`,
          `${d} · ${qv} = ${n}, więc wynik to ${qv}.`,
        ],
        [
          `Divide the hundreds, tens and ones by ${d} in turn.`,
          `${d} × ${qv} = ${n}, so the answer is ${qv}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(3, 9);
      const qv = rng.int(12, 99);
      const r = rng.int(1, d - 1);
      const n = d * qv + r;
      const choice4 = makeChoices(rng, r, [qv, d - r, r + 1], (x) => x.int(0, 8));
      return q(
        both(
          `Oblicz pisemnie ${n} : ${d}. Jaka jest reszta?`,
          `Calculate ${n} ÷ ${d}. What is the remainder?`,
        ),
        numeric(r),
        [`${n} : ${d} = ${qv} r ${r}, bo ${d} · ${qv} = ${d * qv}.`, `${n} − ${d * qv} = ${r}.`],
        [`${n} ÷ ${d} = ${qv} r ${r}, because ${d} × ${qv} = ${d * qv}.`, `${n} − ${d * qv} = ${r}.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 4. order of operations
function orderOfOps(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 30);
      const b = rng.int(2, 9);
      const c = rng.int(2, 9);
      const v = a + b * c;
      const choice4 = makeChoices(rng, v, [(a + b) * c, v + 1, v - 1], (r) => r.int(5, 120));
      return q(
        both(`Oblicz: ${a} + ${b} · ${c} = ?`, `Calculate: ${a} + ${b} × ${c} = ?`),
        numeric(v),
        [`Najpierw mnożenie: ${b} · ${c} = ${b * c}.`, `Potem dodawanie: ${a} + ${b * c} = ${v}.`],
        [`Multiplication first: ${b} × ${c} = ${b * c}.`, `Then addition: ${a} + ${b * c} = ${v}.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 15);
      const b = rng.int(2, 15);
      const c = rng.int(2, 9);
      const v = (a + b) * c;
      const choice4 = makeChoices(rng, v, [a + b * c, v + c, v - c], (r) => r.int(5, 250));
      return q(
        both(`Oblicz: (${a} + ${b}) · ${c} = ?`, `Calculate: (${a} + ${b}) × ${c} = ?`),
        numeric(v),
        [`Najpierw nawias: ${a} + ${b} = ${a + b}.`, `Potem mnożenie: ${a + b} · ${c} = ${v}.`],
        [`Brackets first: ${a} + ${b} = ${a + b}.`, `Then multiply: ${a + b} × ${c} = ${v}.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 9);
      const b = rng.int(2, 9);
      const d = rng.int(2, 9);
      const c = d * rng.int(1, 5);
      const v = a * b - c / d;
      const choice4 = makeChoices(rng, v, [(a * b - c) / d, v + 1, a * b + c / d], (r) => r.int(1, 90));
      return q(
        both(`Oblicz: ${a} · ${b} − ${c} : ${d} = ?`, `Calculate: ${a} × ${b} − ${c} ÷ ${d} = ?`),
        numeric(v),
        [
          `Mnożenie i dzielenie przed odejmowaniem: ${a} · ${b} = ${a * b}, ${c} : ${d} = ${c / d}.`,
          `${a * b} − ${c / d} = ${v}.`,
        ],
        [
          `Multiply and divide before subtracting: ${a} × ${b} = ${a * b}, ${c} ÷ ${d} = ${c / d}.`,
          `${a * b} − ${c / d} = ${v}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 5. fractions compare / add
function fractionsCompare(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const fallback = (n: number, d: number) => (i: number) => {
    const f = simplify(n + i + 1, d + i + 2);
    return frac(f.num, f.den);
  };
  return byTier(
    tier,
    () => {
      const d = rng.int(3, 12);
      const a = rng.int(1, d - 1);
      let b = rng.int(1, d - 1);
      while (b === a) b = rng.int(1, d - 1);
      const bigger = Math.max(a, b);
      const smaller = Math.min(a, b);
      const choice4 = makeTextChoices(
        rng,
        frac(bigger, d),
        [frac(smaller, d), frac(d, bigger), frac(bigger, d + 1)],
        fallback(bigger, d),
      );
      return q(
        both(
          `Która liczba jest większa: ${a}/${d} czy ${b}/${d}? Wpisz ją.`,
          `Which is bigger: ${a}/${d} or ${b}/${d}? Type it.`,
        ),
        fraction(bigger, d, false),
        [
          `Przy tym samym mianowniku większy jest ułamek z większym licznikiem.`,
          `${bigger} > ${smaller}, więc ${bigger}/${d} jest większe.`,
        ],
        [
          `With the same denominator, the fraction with the bigger numerator is bigger.`,
          `${bigger} > ${smaller}, so ${bigger}/${d} is bigger.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.int(3, 12);
      const a = rng.int(1, d - 1);
      const b = rng.int(1, d - a > 0 ? Math.max(1, d - a) : 1);
      const sum = a + b;
      const s = simplify(sum, d);
      const choice4 = makeTextChoices(
        rng,
        frac(s.num, s.den),
        [frac(sum, 2 * d), frac(a * b, d), frac(sum + 1, d)],
        fallback(s.num, s.den),
      );
      return q(
        both(`Oblicz: ${a}/${d} + ${b}/${d} = ?`, `Calculate: ${a}/${d} + ${b}/${d} = ?`),
        fraction(s.num, s.den),
        [
          `Ten sam mianownik: dodajemy liczniki: ${a} + ${b} = ${sum}.`,
          `Wynik: ${sum}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Same denominator: add the numerators: ${a} + ${b} = ${sum}.`,
          `Result: ${sum}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const d = rng.pick([4, 6, 8, 10, 12]);
      const small = rng.pick([2, 3, 4, 5, 6].filter((x) => d % x === 0 && x < d));
      const a = rng.int(1, small - 1);
      const b = rng.int(1, d - 1);
      const na = a * (d / small);
      const sum = na + b;
      const s = simplify(sum, d);
      const choice4 = makeTextChoices(
        rng,
        frac(s.num, s.den),
        [frac(a + b, d + small), frac(a + b, d), frac(sum, 2 * d)],
        fallback(s.num, s.den),
      );
      return q(
        both(`Oblicz: ${a}/${small} + ${b}/${d} = ?`, `Calculate: ${a}/${small} + ${b}/${d} = ?`),
        fraction(s.num, s.den),
        [
          `Sprowadzamy do mianownika ${d}: ${a}/${small} = ${na}/${d}.`,
          `${na}/${d} + ${b}/${d} = ${sum}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        [
          `Convert to denominator ${d}: ${a}/${small} = ${na}/${d}.`,
          `${na}/${d} + ${b}/${d} = ${sum}/${d}${s.den !== d ? ` = ${s.num}/${s.den}` : ""}.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 6. decimals intro
function decimalsIntro(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = clean(rng.int(1, 99) / 10);
      let b = clean(rng.int(1, 99) / 10);
      while (b === a) b = clean(rng.int(1, 99) / 10);
      const bigger = Math.max(a, b);
      const choice4 = makeChoices(
        rng,
        bigger,
        [Math.min(a, b), clean(bigger + 0.1), clean(bigger * 10)],
        (r) => clean(r.int(1, 99) / 10),
      );
      return q(
        both(
          `Która liczba jest większa: ${numStr(a, "pl")} czy ${numStr(b, "pl")}? Wpisz ją.`,
          `Which is bigger: ${numStr(a, "en")} or ${numStr(b, "en")}? Type it.`,
        ),
        numeric(bigger),
        [
          `Porównujemy najpierw części całkowite, potem cyfry po przecinku.`,
          `Większa jest ${numStr(bigger, "pl")}.`,
        ],
        [
          `Compare the whole parts first, then the digits after the point.`,
          `The bigger one is ${numStr(bigger, "en")}.`,
        ],
        { choice4 },
      );
    },
    () => {
      const zl = rng.int(1, 30);
      const gr = rng.pick([5, 10, 25, 50, 75, 99]);
      const value = zl * 100 + gr;
      const choice4 = makeChoices(
        rng,
        value,
        [zl * 10 + gr, zl + gr, value + 10],
        (r) => r.int(100, 3500),
        (n) => numUnit(n, "gr"),
      );
      return q(
        both(
          `Ile groszy to ${zl},${String(gr).padStart(2, "0")} zł?`,
          `How many grosz is ${zl}.${String(gr).padStart(2, "0")} zł?`,
        ),
        numeric(value, { pl: "gr", en: "gr" }),
        [`1 zł = 100 gr, więc ${zl} zł = ${zl * 100} gr.`, `${zl * 100} + ${gr} = ${value} gr.`],
        [`1 zł = 100 gr, so ${zl} zł = ${zl * 100} gr.`, `${zl * 100} + ${gr} = ${value} gr.`],
        { choice4 },
      );
    },
    () => {
      const a = clean(rng.int(100, 4999) / 100);
      const b = clean(rng.int(100, 2999) / 100);
      const value = clean(a + b);
      const choice4 = makeChoices(
        rng,
        value,
        [clean(a - b), clean(value + 0.1), clean(value + 1)],
        (r) => clean(r.int(100, 8000) / 100),
        (n) => numUnit(n, "zł"),
      );
      return q(
        both(
          `Zeszyt kosztuje ${numStr(a, "pl")} zł, a długopis ${numStr(b, "pl")} zł. Ile kosztują razem?`,
          `A notebook costs ${numStr(a, "en")} zł and a pen ${numStr(b, "en")} zł. How much together?`,
        ),
        numeric(value, { pl: "zł", en: "zł" }),
        [
          `Dodajemy złotówki i grosze, przecinek pod przecinkiem.`,
          `${numStr(a, "pl")} + ${numStr(b, "pl")} = ${numStr(value, "pl")} zł.`,
        ],
        [
          `Add złoty and grosz, decimal point under decimal point.`,
          `${numStr(a, "en")} + ${numStr(b, "en")} = ${numStr(value, "en")} zł.`,
        ],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 7. rectangle area & perimeter
function areaRect(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const a = rng.int(2, 12);
      const b = rng.int(2, 12);
      const area = a * b;
      const choice4 = makeChoices(rng, area, [2 * (a + b), a + b, area + a], (r) => r.int(4, 150), cm2);
      return q(
        both(
          `Prostokąt ma boki ${a} cm i ${b} cm. Jakie jest jego pole?`,
          `A rectangle has sides ${a} cm and ${b} cm. What is its area?`,
        ),
        numeric(area, CM2),
        [`Pole prostokąta = a · b.`, `${a} · ${b} = ${area} cm².`],
        [`Area of a rectangle = a × b.`, `${a} × ${b} = ${area} cm².`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 15);
      const b = rng.int(2, 15);
      const per = 2 * (a + b);
      const choice4 = makeChoices(rng, per, [a * b, a + b, per + 2], (r) => r.int(8, 60), cm);
      return q(
        both(
          `Prostokąt ma boki ${a} cm i ${b} cm. Jaki jest jego obwód?`,
          `A rectangle has sides ${a} cm and ${b} cm. What is its perimeter?`,
        ),
        numeric(per, CM),
        [`Obwód = 2 · (a + b).`, `2 · (${a} + ${b}) = ${per} cm.`],
        [`Perimeter = 2 × (a + b).`, `2 × (${a} + ${b}) = ${per} cm.`],
        { choice4 },
      );
    },
    () => {
      const a = rng.int(2, 12);
      const b = rng.int(2, 12);
      const area = a * b;
      const choice4 = makeChoices(rng, b, [area - a, b + 1, area / 2], (r) => r.int(2, 20), cm);
      return q(
        both(
          `Pole prostokąta wynosi ${area} cm², a jeden bok ma ${a} cm. Ile ma drugi bok?`,
          `The area of a rectangle is ${area} cm² and one side is ${a} cm. How long is the other side?`,
        ),
        numeric(b, CM),
        [`Pole = a · b, więc b = pole : a.`, `${area} : ${a} = ${b} cm.`],
        [`Area = a × b, so b = area ÷ a.`, `${area} ÷ ${a} = ${b} cm.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 8. unit conversion
function unitsConvert(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      if (rng.chance(0.5)) {
        const m = rng.int(2, 50);
        const choice4 = makeChoices(
          rng,
          m * 100,
          [m * 10, m * 1000, m + 100],
          (r) => r.int(2, 50) * 100,
          (n) => numUnit(n, "cm"),
        );
        return q(
          both(`${m} m = ? cm`, `${m} m = ? cm`),
          numeric(m * 100, CM),
          [`1 m = 100 cm.`, `${m} · 100 = ${m * 100} cm.`],
          [`1 m = 100 cm.`, `${m} × 100 = ${m * 100} cm.`],
          { choice4 },
        );
      }
      const km = rng.int(2, 30);
      const choice4 = makeChoices(
        rng,
        km * 1000,
        [km * 100, km * 10, km + 1000],
        (r) => r.int(2, 30) * 1000,
        (n) => numUnit(n, "m"),
      );
      return q(
        both(`${km} km = ? m`, `${km} km = ? m`),
        numeric(km * 1000, { pl: "m", en: "m" }),
        [`1 km = 1000 m.`, `${km} · 1000 = ${km * 1000} m.`],
        [`1 km = 1000 m.`, `${km} × 1000 = ${km * 1000} m.`],
        { choice4 },
      );
    },
    () => {
      if (rng.chance(0.5)) {
        const kg = rng.int(2, 20);
        const choice4 = makeChoices(
          rng,
          kg * 1000,
          [kg * 100, kg * 10, kg + 1000],
          (r) => r.int(2, 20) * 1000,
          (n) => numUnit(n, "g"),
        );
        return q(
          both(`${kg} kg = ? g`, `${kg} kg = ? g`),
          numeric(kg * 1000, { pl: "g", en: "g" }),
          [`1 kg = 1000 g.`, `${kg} · 1000 = ${kg * 1000} g.`],
          [`1 kg = 1000 g.`, `${kg} × 1000 = ${kg * 1000} g.`],
          { choice4 },
        );
      }
      const h = rng.int(2, 9);
      const choice4 = makeChoices(
        rng,
        h * 60,
        [h * 100, h * 30, h + 60],
        (r) => r.int(2, 9) * 60,
        (n) => numUnit(n, "min"),
      );
      return q(
        both(`${h} h = ? min`, `${h} h = ? min`),
        numeric(h * 60, { pl: "min", en: "min" }),
        [`1 godzina = 60 minut.`, `${h} · 60 = ${h * 60} min.`],
        [`1 hour = 60 minutes.`, `${h} × 60 = ${h * 60} min.`],
        { choice4 },
      );
    },
    () => {
      if (rng.chance(0.5)) {
        const km = rng.int(1, 9);
        const m = rng.int(1, 9) * 50;
        const value = km * 1000 + m;
        const choice4 = makeChoices(
          rng,
          value,
          [km * 100 + m, km + m, value + 100],
          (r) => r.int(1000, 9999),
          (n) => numUnit(n, "m"),
        );
        return q(
          both(`${km} km ${m} m = ? m`, `${km} km ${m} m = ? m`),
          numeric(value, { pl: "m", en: "m" }),
          [`${km} km = ${km * 1000} m.`, `${km * 1000} + ${m} = ${value} m.`],
          [`${km} km = ${km * 1000} m.`, `${km * 1000} + ${m} = ${value} m.`],
          { choice4 },
        );
      }
      const h = rng.int(1, 5);
      const min = rng.int(1, 11) * 5;
      const value = h * 60 + min;
      const choice4 = makeChoices(
        rng,
        value,
        [h * 100 + min, h + min, value + 10],
        (r) => r.int(60, 400),
        (n) => numUnit(n, "min"),
      );
      return q(
        both(`${h} h ${min} min = ? min`, `${h} h ${min} min = ? min`),
        numeric(value, { pl: "min", en: "min" }),
        [`${h} h = ${h * 60} min.`, `${h * 60} + ${min} = ${value} min.`],
        [`${h} h = ${h * 60} min.`, `${h * 60} + ${min} = ${value} min.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 9. angle types
function anglesTypes(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const kinds = [
    { id: "ostry", pl: "ostry", en: "acute" },
    { id: "prosty", pl: "prosty", en: "right" },
    { id: "rozwarty", pl: "rozwarty", en: "obtuse" },
    { id: "półpełny", pl: "półpełny", en: "straight" },
  ];
  const deg = byTier(
    tier,
    () => rng.pick([rng.int(10, 80), 90, rng.int(100, 170)]),
    () => rng.pick([rng.int(5, 89), 90, rng.int(91, 179), 180]),
    () => rng.pick([rng.int(85, 89), 90, rng.int(91, 95), 180, rng.int(170, 179)]),
  );
  const kind = deg < 90 ? kinds[0]! : deg === 90 ? kinds[1]! : deg < 180 ? kinds[2]! : kinds[3]!;
  const choice4 = makeTextChoices(
    rng,
    { pl: kind.pl, en: kind.en },
    kinds.filter((k) => k !== kind).map((k) => ({ pl: k.pl, en: k.en })),
  );
  return q(
    both(`Kąt ma ${deg}°. Jaki to rodzaj kąta?`, `An angle measures ${deg}°. What kind of angle is it?`),
    choice4,
    [
      `Ostry: mniej niż 90°, prosty: 90°, rozwarty: między 90° a 180°, półpełny: 180°.`,
      `${deg}° to kąt ${kind.pl}.`,
    ],
    [
      `Acute: less than 90°, right: 90°, obtuse: between 90° and 180°, straight: 180°.`,
      `${deg}° is a ${kind.en} angle.`,
    ],
    { choice4 },
  );
}

// ---------------------------------------------------------------- 10. scale
function scale(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  return byTier(
    tier,
    () => {
      const s = rng.pick([10, 50, 100]);
      const map = rng.int(2, 9);
      const real = map * s;
      const choice4 = makeChoices(rng, real, [map + s, real / 10, real * 10], (r) => r.int(1, 9) * 100, cm);
      return q(
        both(
          `Plan narysowano w skali 1:${s}. Odcinek na planie ma ${map} cm. Ile ma w rzeczywistości?`,
          `A plan is drawn at scale 1:${s}. A segment on the plan is ${map} cm. How long is it in reality?`,
        ),
        numeric(real, CM),
        [
          `Skala 1:${s} oznacza, że 1 cm na planie to ${s} cm w rzeczywistości.`,
          `${map} · ${s} = ${real} cm.`,
        ],
        [`Scale 1:${s} means 1 cm on the plan is ${s} cm in reality.`, `${map} × ${s} = ${real} cm.`],
        { choice4 },
      );
    },
    () => {
      const s = rng.pick([1000, 2000, 5000]);
      const map = rng.int(2, 9);
      const realM = (map * s) / 100;
      const choice4 = makeChoices(
        rng,
        realM,
        [map * s, realM * 10, realM / 10],
        (r) => r.int(1, 50) * 10,
        (n) => numUnit(n, "m"),
      );
      return q(
        both(
          `Mapa ma skalę 1:${s}. Odcinek na mapie ma ${map} cm. Ile metrów ma w rzeczywistości?`,
          `A map has scale 1:${s}. A segment on the map is ${map} cm. How many metres is that in reality?`,
        ),
        numeric(realM, { pl: "m", en: "m" }),
        [`${map} cm · ${s} = ${map * s} cm w rzeczywistości.`, `${map * s} cm = ${realM} m.`],
        [`${map} cm × ${s} = ${map * s} cm in reality.`, `${map * s} cm = ${realM} m.`],
        { choice4 },
      );
    },
    () => {
      const s = rng.pick([100, 200, 500]);
      const map = rng.int(2, 12);
      const realM = (map * s) / 100;
      const choice4 = makeChoices(rng, map, [realM, map * 10, map + 1], (r) => r.int(1, 20), cm);
      return q(
        both(
          `Pokój ma ${realM} m długości. Ile centymetrów ma na planie w skali 1:${s}?`,
          `A room is ${realM} m long. How many centimetres is it on a plan at scale 1:${s}?`,
        ),
        numeric(map, CM),
        [`${realM} m = ${realM * 100} cm.`, `${realM * 100} : ${s} = ${map} cm na planie.`],
        [`${realM} m = ${realM * 100} cm.`, `${realM * 100} ÷ ${s} = ${map} cm on the plan.`],
        { choice4 },
      );
    },
  );
}

// ---------------------------------------------------------------- 11. Roman numerals
const ROMAN: Array<[number, string]> = [
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];
export function toRoman(n: number): string {
  let out = "";
  let rest = n;
  for (const [v, sym] of ROMAN) {
    while (rest >= v) {
      out += sym;
      rest -= v;
    }
  }
  return out;
}

function romanNumerals(rng: Rng, tier: 1 | 2 | 3): QuestionDraft {
  const n = byTier(
    tier,
    () => rng.int(1, 20),
    () => rng.int(11, 39),
    () => rng.int(20, 100),
  );
  const roman = toRoman(n);
  if (tier < 3) {
    const choice4 = makeChoices(rng, n, [n + 1, n - 1, n + 10], (r) => r.int(1, 40));
    return q(
      both(`Jaką liczbę oznacza ${roman}?`, `Which number is ${roman}?`),
      numeric(n),
      [`I = 1, V = 5, X = 10, L = 50; mniejszy znak przed większym odejmujemy.`, `${roman} = ${n}.`],
      [
        `I = 1, V = 5, X = 10, L = 50; a smaller symbol before a bigger one is subtracted.`,
        `${roman} = ${n}.`,
      ],
      { choice4 },
    );
  }
  const choice4 = makeTextChoices(
    rng,
    { pl: roman, en: roman },
    [toRoman(n + 1), toRoman(n - 1), toRoman(n + 10)].map((r) => ({ pl: r, en: r })),
    (i) => ({ pl: toRoman(n + i + 2), en: toRoman(n + i + 2) }),
  );
  return q(
    both(`Zapisz liczbę ${n} cyframi rzymskimi.`, `Write ${n} in Roman numerals.`),
    textAnswer(roman),
    [
      `Rozbijamy: ${n} = ${[Math.floor(n / 10) * 10, n % 10].filter((x) => x > 0).join(" + ")}.`,
      `Zapis: ${roman}.`,
    ],
    [
      `Split: ${n} = ${[Math.floor(n / 10) * 10, n % 10].filter((x) => x > 0).join(" + ")}.`,
      `Written: ${roman}.`,
    ],
    { choice4 },
  );
}

const def = (
  id: string,
  pl: string,
  en: string,
  generate: (rng: Rng, tier: 1 | 2 | 3) => QuestionDraft,
  modes: TopicDef["modes"] = ["numeric", "choice4"],
): TopicDef => ({
  id,
  level: "sp4",
  name: text(pl, en),
  modes,
  generate,
});

export const SP4_TOPICS: readonly TopicDef[] = [
  def("sp4.big_numbers", "Duże liczby i zaokrąglanie", "Big numbers and rounding", bigNumbers),
  def("sp4.written_mult", "Mnożenie pisemne", "Written multiplication", writtenMult),
  def("sp4.written_div", "Dzielenie pisemne", "Written division", writtenDiv),
  def("sp4.order_of_ops", "Kolejność działań", "Order of operations", orderOfOps),
  def(
    "sp4.fractions_compare",
    "Ułamki – porównywanie i dodawanie",
    "Fractions – comparing and adding",
    fractionsCompare,
    ["fraction", "choice4"],
  ),
  def("sp4.decimals_intro", "Ułamki dziesiętne – początki", "Decimals – basics", decimalsIntro),
  def("sp4.area_rect", "Pole i obwód prostokąta", "Area and perimeter of a rectangle", areaRect),
  def("sp4.units_convert", "Jednostki", "Units", unitsConvert),
  def("sp4.angles_types", "Rodzaje kątów", "Types of angles", anglesTypes, ["choice4"]),
  def("sp4.scale", "Skala", "Scale", scale),
  def("sp4.roman_numerals", "Liczby rzymskie", "Roman numerals", romanNumerals, [
    "numeric",
    "text",
    "choice4",
  ]),
];
